import {
	VIEW_PATH_MARKER,
	viewPathSuffix,
} from "@lobu/core/contracts/tools/view-path";

/**
 * An event's own page is `/{owner}/events/<id>`; the views that render it live
 * beneath it at `/{owner}/events/<id>/-/views/<key>`. One parser and one
 * builder, shared by the routes, the peek pane and its URL state, so the
 * forms cannot drift apart.
 */

/**
 * The event id an event-page segment names. An event id is a positive integer
 * written in plain decimal; anything else (`deal`, `1e3`, `07`) is not an
 * event, so the path is a not-found or some other page.
 */
export function parseEventId(segment: string): number | null {
	const n = Number(segment);
	return Number.isSafeInteger(n) && n > 0 && String(n) === segment ? n : null;
}

interface EventPath {
	owner: string;
	eventId: number;
	viewKey?: string;
}

/**
 * Parse an app pathname as an event page or event view page. Exactly those two
 * shapes: `/events/<id>/<anything else>` is left to the record-activity form
 * (`/events/<type>/<slug>`), whose type slug may itself be numeric.
 */
export function parseEventPath(pathname: string): EventPath | null {
	const segments = pathname.split("/").filter(Boolean);
	const [owner, section, idSegment] = segments;
	if (!owner || section !== "events" || idSegment === undefined) return null;
	const eventId = parseEventId(idSegment);
	if (eventId === null) return null;
	if (segments.length === 3) return { owner, eventId };
	const viewKey = segments[5];
	if (
		segments.length === 6 &&
		segments[3] === VIEW_PATH_MARKER &&
		segments[4] === "views" &&
		viewKey &&
		// A view key is URL-safe as written; anything that needed escaping is
		// not a view key, and would otherwise be carried into the pane's URL.
		encodeURIComponent(viewKey) === viewKey
	) {
		return { owner, eventId, viewKey };
	}
	return null;
}

/** The event page, or one of its views when `viewKey` is set. */
export function eventPageHref(
	owner: string,
	eventId: number,
	viewKey?: string,
): string {
	return `/${owner}/events/${eventId}${viewKey ? viewPathSuffix(viewKey) : ""}`;
}
